/**
 * Ficha de un contacto de la base (`/activa/oficial/base/contactos/:id`), SPEC-BD §6.3.
 *
 * - El teléfono va enmascarado. «Ver número completo» lo pide con `?reveal=true` y el backend deja
 *   registrado quién lo vio (sin el número). No se guarda en ningún lado del navegador. Es del equipo: a una
 *   persona de un cliente no se le ofrece (el backend igual se lo contesta enmascarado, 27/10).
 * - El consentimiento se cambia sólo con motivo (y, para registrarlo, con cómo, cuándo y constancia): es
 *   la constancia del art. 5 de la Ley 25.326, y el historial la muestra entera.
 * - «Borrar este contacto» es el derecho de supresión (art. 16): se borra de verdad y deja una constancia
 *   sin teléfono. Al terminar se vuelve a la lista con el aviso.
 * - Si la persona le escribió al número, «Ver la conversación» lleva a la bandeja con `?filtro=todas` (con el
 *   filtro por defecto, una conversación ya respondida no aparecía en la lista de al lado). Si el pedido
 *   falla, el link no aparece y la ficha sigue igual: es un atajo, no un dato de la ficha.
 * - Las bajas, las campañas y las respuestas del simulado: en twilio no vienen (como la no contactar y el
 *   «Puede recibir»); en simulado vienen con su marca, y una «BAJA» de la demo no se lee como una persona
 *   que pidió la baja. Una baja sólo del simulado tampoco cuenta como «ya está en no contactar» al
 *   borrar el contacto: el backend deja una real, y la casilla se tiene que poder elegir.
 */

import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Eye, Loader2, MessagesSquare, OctagonAlert, Trash2 } from "lucide-react";
import type { CambioConsentimiento, ContactoFicha, EntradaSupresion, OpcionesBase, ResultadoSupresion } from "../../types.base";
import { baseApi } from "../../baseApi";
import { conversacionesApi } from "../../conversacionesApi";
import { R_CONVERSACIONES } from "../../logic/conversaciones";
import { destinoParaMostrar, nombreDeQuienParaCliente } from "../../logic/formato";
import { normalizarTexto } from "../../logic/base";
import { Tarjeta } from "../../components/Tarjeta";
import { ChipEstadoMensaje } from "../../components/ChipEstadoMensaje";
import { ChipSimuladoNoSalio } from "../../components/ChipSimuladoNoSalio";
import { fecha, fechaHora } from "../../../campana-nueva/utils/format";
import { CajaError, FUENTE_BASE, R_BASE, R_CARGAS, llamar, textoFallo } from "../../components/base/comun";
import { ChipConsentimiento } from "../../components/base/ChipConsentimiento";
import { InputEtiquetas } from "../../components/base/InputEtiquetas";
import { ModalConsentimiento } from "../../components/base/ModalConsentimiento";
import { ModalSuprimir } from "../../components/base/ModalSuprimir";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

const MAX_ETIQUETAS_CONTACTO = 30;

/** Un día "YYYY-MM-DD" se muestra al mediodía: a las 00:00 UTC cae el día anterior en Argentina. */
const dia = (d: string | null | undefined) => (d ? fecha(/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : d) : "—");

export default function ContactoFichaPage() {
  const equipo = useEsEquipoDeenex();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [ficha, setFicha] = useState<ContactoFicha | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [revelando, setRevelando] = useState(false);
  const [errorRevelar, setErrorRevelar] = useState("");
  const [sugerencias, setSugerencias] = useState<{ clave: string; texto: string }[]>([]);
  const [guardandoEtiquetas, setGuardandoEtiquetas] = useState(false);
  const [errorEtiquetas, setErrorEtiquetas] = useState("");

  const [modalConsentimiento, setModalConsentimiento] = useState<"registrar" | "quitar" | null>(null);
  const [guardandoConsentimiento, setGuardandoConsentimiento] = useState(false);
  const [errorConsentimiento, setErrorConsentimiento] = useState("");
  const [enNoContactar, setEnNoContactar] = useState(false);

  const [conversacionId, setConversacionId] = useState<string | null>(null);

  const [modalSuprimir, setModalSuprimir] = useState(false);
  const [suprimiendo, setSuprimiendo] = useState(false);
  const [errorSuprimir, setErrorSuprimir] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    const l = await llamar<ContactoFicha>(() => baseApi.contacto(id));
    setCargando(false);
    if (!l.ok || !l.data) {
      if (!l.ok && l.status === 404) { setNoEncontrado(true); return; }
      setError(l.ok ? "El servidor no devolvió el contacto." : textoFallo(l));
      return;
    }
    setFicha(l.data);
    setError("");
  }, [id]);

  useEffect(() => { setNoEncontrado(false); setFicha(null); void cargar(); }, [cargar]);

  useEffect(() => {
    void (async () => {
      const l = await llamar<OpcionesBase>(() => baseApi.opciones());
      if (l.ok && Array.isArray(l.data?.etiquetas)) setSugerencias(l.data.etiquetas);
    })();
  }, []);

  // La conversación de este contacto, si le escribió al número. `vivo`: al pasar a otro contacto, la
  // respuesta del anterior no puede poner su link en esta ficha.
  useEffect(() => {
    let vivo = true;
    setConversacionId(null);
    void (async () => {
      const l = await llamar<{ conversacionId: string | null }>(() => conversacionesApi.porContacto(id));
      if (vivo && l.ok && typeof l.data?.conversacionId === "string" && l.data.conversacionId) setConversacionId(l.data.conversacionId);
    })();
    return () => { vivo = false; };
  }, [id]);

  const revelar = async () => {
    setRevelando(true);
    setErrorRevelar("");
    const l = await llamar<ContactoFicha>(() => baseApi.contacto(id, true));
    setRevelando(false);
    if (!l.ok || !l.data) { setErrorRevelar(l.ok ? "El servidor no devolvió el número." : textoFallo(l)); return; }
    setFicha(l.data);
  };

  /** Las etiquetas se guardan en el momento: lo que se sumó va en `agregar`, lo que se sacó en `quitar` (por clave). */
  const cambiarEtiquetas = async (nuevas: string[]) => {
    if (!ficha) return;
    const actuales = ficha.etiquetas;
    const clavesNuevas = new Set(nuevas.map(normalizarTexto));
    const clavesActuales = new Set(actuales.map((e) => normalizarTexto(e.texto)));
    const agregar = nuevas.filter((t) => !clavesActuales.has(normalizarTexto(t)));
    const quitar = actuales.filter((e) => !clavesNuevas.has(normalizarTexto(e.texto))).map((e) => e.clave);
    if (!agregar.length && !quitar.length) return;
    setGuardandoEtiquetas(true);
    setErrorEtiquetas("");
    const l = await llamar<ContactoFicha>(() => baseApi.cambiarEtiquetas(ficha.id, {
      ...(agregar.length ? { agregar } : {}), ...(quitar.length ? { quitar } : {}),
    }));
    setGuardandoEtiquetas(false);
    if (!l.ok || !l.data) { setErrorEtiquetas(l.ok ? "El servidor no devolvió el contacto." : textoFallo(l)); return; }
    // La respuesta trae el número enmascarado: si estaba revelado, se conserva lo que ya se vio.
    setFicha((f) => (f && !f.enmascarado ? { ...l.data, destino: f.destino, enmascarado: false } : l.data));
  };

  const cambiarConsentimiento = async (c: CambioConsentimiento) => {
    if (!ficha) return;
    setGuardandoConsentimiento(true);
    setErrorConsentimiento("");
    setEnNoContactar(false);
    const l = await llamar<ContactoFicha>(() => baseApi.cambiarConsentimiento(ficha.id, c));
    setGuardandoConsentimiento(false);
    if (!l.ok || !l.data) {
      setErrorConsentimiento(l.ok ? "El servidor no devolvió el contacto." : textoFallo(l));
      setEnNoContactar(!l.ok && l.code === "EN_NO_CONTACTAR");
      return;
    }
    setFicha((f) => (f && !f.enmascarado ? { ...l.data, destino: f.destino, enmascarado: false } : l.data));
    setModalConsentimiento(null);
  };

  const suprimir = async (s: EntradaSupresion) => {
    if (!ficha) return;
    setSuprimiendo(true);
    setErrorSuprimir("");
    const l = await llamar<ResultadoSupresion>(() => baseApi.suprimirContacto(ficha.id, s));
    setSuprimiendo(false);
    if (!l.ok || !l.data?.suprimido) { setErrorSuprimir(l.ok ? "El servidor no confirmó el borrado." : textoFallo(l)); return; }
    navigate(`${R_BASE}/contactos`, { replace: true, state: { aviso: "Contacto borrado. Quedó la constancia de la supresión." } });
  };

  const volver = (
    <Link to={`${R_BASE}/contactos`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
      <ArrowLeft size={13} /> Contactos
    </Link>
  );

  if (noEncontrado) {
    return (
      <div className="space-y-4">
        {volver}
        <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-slate-600">No encontramos este contacto (puede que se haya borrado).</p>
          <Link to={`${R_BASE}/contactos`} className="mt-4 h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center">
            Volver a Contactos
          </Link>
        </div>
      </div>
    );
  }
  if (cargando && !ficha) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Cargando">
        {volver}
        <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
        <div className="grid gap-5 lg:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      </div>
    );
  }
  if (!ficha) return <div className="space-y-4">{volver}<CajaError titulo="No pudimos traer el contacto." detalle={error} onReintentar={() => void cargar()} /></div>;

  const nombreCompleto = [ficha.nombre, ficha.apellido].filter((x) => x && x.trim()).join(" ");
  const bajasActivas = ficha.noContactar.filter((b) => b.activo);
  const optIn = ficha.consentimiento.optIn;

  return (
    <div className="space-y-5">
      {volver}

      <header className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-slate-900 tracking-tight">{nombreCompleto || "Sin nombre"}</h3>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-sm">
            <span className="font-mono text-slate-700" data-testid="telefono-contacto">
              {ficha.enmascarado ? destinoParaMostrar(ficha.destino, false) : destinoParaMostrar(ficha.destino, true)}
            </span>
            {ficha.enmascarado && equipo && (
              <>
                <button type="button" onClick={revelar} disabled={revelando}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline disabled:opacity-50">
                  {revelando ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Ver número completo
                </button>
                <span className="text-[11px] text-slate-400">Queda registrado que lo viste</span>
              </>
            )}
          </div>
          {errorRevelar && <p className="text-xs text-red-600 mt-1" role="alert">{errorRevelar}</p>}
          {conversacionId && (
            <Link to={`${R_CONVERSACIONES}/${encodeURIComponent(conversacionId)}?filtro=todas`}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline">
              <MessagesSquare size={12} /> Ver la conversación
            </Link>
          )}
        </div>
        {ficha.puedeRecibir.ok ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold px-3 py-1.5">
            <CheckCircle2 size={15} /> Puede recibir campañas
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 text-sm font-semibold px-3 py-1.5">
            <OctagonAlert size={15} /> {ficha.puedeRecibir.texto || "No puede recibir campañas"}
          </span>
        )}
      </header>

      <div className="grid gap-5 lg:grid-cols-2">
        <Tarjeta titulo="Datos">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Dato label="Nombre" valor={ficha.nombre} />
            <Dato label="Apellido" valor={ficha.apellido} />
            <Dato label="Email" valor={ficha.email} />
            <Dato label="Sucursal" valor={ficha.sucursal} />
            {ficha.campos.map((c) => <Dato key={c.clave} label={c.etiqueta} valor={c.tipo === "fecha" ? dia(c.valor) : c.valor} />)}
          </dl>
          <p className="text-[11px] text-slate-400 mt-3">Alta: {fechaHora(ficha.alta)} · Actualizado: {fechaHora(ficha.actualizado)}</p>
        </Tarjeta>

        <Tarjeta titulo="Etiquetas">
          <InputEtiquetas
            valores={ficha.etiquetas.map((e) => e.texto)}
            onChange={(v) => void cambiarEtiquetas(v)}
            sugerencias={sugerencias}
            max={MAX_ETIQUETAS_CONTACTO}
            disabled={guardandoEtiquetas || !equipo}
            ayuda={equipo ? "Se guardan en el momento." : "Las etiquetas las pone el equipo de Deenex."}
          />
          {guardandoEtiquetas && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Guardando…</p>}
          {errorEtiquetas && <p className="text-xs text-red-600 mt-1" role="alert">{errorEtiquetas}</p>}
        </Tarjeta>

        <Tarjeta titulo="Consentimiento" right={<ChipConsentimiento valor={optIn} grande />}>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <Dato label="De dónde" valor={ficha.consentimiento.fuente ? FUENTE_BASE[ficha.consentimiento.fuente] ?? ficha.consentimiento.fuente : ""} />
            <Dato label="Detalle" valor={ficha.consentimiento.detalle} />
            <Dato label="Fecha" valor={ficha.consentimiento.fecha ? dia(ficha.consentimiento.fecha) : ""} />
            <Dato label="Carga de origen" valor={ficha.consentimiento.carga ? (
              <Link to={`${R_CARGAS}/${encodeURIComponent(ficha.consentimiento.carga.id)}`} className="text-violet-700 hover:underline">{ficha.consentimiento.carga.nombre}</Link>
            ) : ""} />
          </dl>
          {/* Registrar o quitar el consentimiento es del equipo de Deenex: un cliente lo ve. */}
          {equipo && <div className="mt-4 flex gap-2 flex-wrap">
            {optIn !== true && (
              <button type="button" onClick={() => { setErrorConsentimiento(""); setEnNoContactar(false); setModalConsentimiento("registrar"); }}
                className="h-9 px-3 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                Registrar consentimiento
              </button>
            )}
            {optIn !== false && (
              <button type="button" onClick={() => { setErrorConsentimiento(""); setEnNoContactar(false); setModalConsentimiento("quitar"); }}
                className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Quitar consentimiento
              </button>
            )}
          </div>}
          <div className="mt-5">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Historial</h4>
            {ficha.historialConsentimiento.length ? (
              <ul className="space-y-2 text-sm">
                {[...ficha.historialConsentimiento].reverse().map((h, i) => (
                  <li key={`${h.at}-${i}`} className="border-l-2 border-slate-100 pl-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ChipConsentimiento valor={h.optIn} />
                      {/* `por` es el mail del token: a una persona de un cliente, alguien del equipo es «Equipo Deenex». */}
                      <span className="text-xs text-slate-500">{fechaHora(h.at)} · {(equipo ? h.por : nombreDeQuienParaCliente(h.por)) || "—"}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {FUENTE_BASE[h.fuente] ?? h.fuente}{h.detalle ? `: ${h.detalle}` : ""}{h.fecha ? ` · del ${dia(h.fecha)}` : ""}{h.carga ? ` · carga «${h.carga.nombre}»` : ""}
                    </p>
                    {h.motivo && <p className="text-xs text-slate-500 italic">Motivo: {h.motivo}</p>}
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-slate-500">Sin cambios registrados.</p>}
          </div>
        </Tarjeta>

        <Tarjeta titulo="No contactar">
          {bajasActivas.length ? (
            <ul className="space-y-2 text-sm">
              {bajasActivas.map((b, i) => (
                <li key={i} className="rounded-xl bg-red-50 px-3 py-2 text-red-800">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{b.canal || "Todos los canales"}</p>
                    {b.simulado === true && <ChipSimuladoNoSalio />}
                  </div>
                  <p className="text-xs">{b.motivo || "Sin motivo"} · desde {fecha(b.desde || undefined)}</p>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-600">No está en la lista de no contactar</p>}
        </Tarjeta>

        <Tarjeta titulo="Cargas de origen">
          {ficha.cargas.length ? (
            <ul className="space-y-1.5 text-sm">
              {ficha.cargas.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3">
                  <Link to={`${R_CARGAS}/${encodeURIComponent(c.id)}`} className="text-violet-700 hover:underline truncate">{c.nombre}</Link>
                  <span className="text-xs text-slate-500 whitespace-nowrap">{fecha(c.fecha)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500">Sin cargas.</p>}
        </Tarjeta>

        <Tarjeta titulo="Campañas que recibió">
          {ficha.campanas.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-50">
                  {ficha.campanas.map((c) => (
                    <tr key={c.campanaId}>
                      <td className="py-1.5 pr-3">
                        <Link to={`/activa/oficial/campanas/${encodeURIComponent(c.campanaId)}`} className="text-violet-700 hover:underline">{c.nombre}</Link>
                      </td>
                      <td className="py-1.5 pr-3"><ChipEstadoMensaje estado={c.estado} /></td>
                      <td className="py-1.5 pr-3 text-xs text-slate-500 whitespace-nowrap">{fecha(c.fecha || undefined)}</td>
                      <td className="py-1.5 text-xs text-slate-600 whitespace-nowrap">
                        {c.respondio && <span>respondió</span>}
                        {c.baja && <span className="ml-1 text-red-700 font-semibold">se dio de baja</span>}
                        {c.simulado === true && <span className="ml-1"><ChipSimuladoNoSalio /></span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-slate-500">Todavía no recibió campañas por el oficial.</p>}
        </Tarjeta>

        <Tarjeta titulo="Respuestas">
          {ficha.respuestas.length ? (
            <ul className="space-y-2 text-sm">
              {ficha.respuestas.map((r) => (
                <li key={r.id}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{fechaHora(r.createdAt)}</span>
                    {r.esBaja && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">BAJA</span>}
                    {r.simulado === true && (
                      <span title="La escribió el simulador: nadie la mandó por WhatsApp." className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">simulada</span>
                    )}
                  </div>
                  <p className="text-slate-700 break-words">{r.texto || "—"}</p>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500">Sin respuestas.</p>}
        </Tarjeta>

        <Tarjeta titulo="Públicos fijos">
          {ficha.publicosFijos.length ? (
            <ul className="flex flex-wrap gap-1.5">
              {ficha.publicosFijos.map((p) => (
                <li key={p.id}>
                  <Link to={`${R_BASE}/publicos/${encodeURIComponent(p.id)}`} className="rounded-full bg-slate-100 text-slate-700 text-xs font-semibold px-2.5 py-0.5 hover:bg-slate-200">{p.nombre}</Link>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500">No está en ningún público fijo.</p>}
        </Tarjeta>
      </div>

      {/* La supresión (Ley 25.326) la ejecuta Deenex: un cliente se la pide a su contacto. */}
      {equipo ? (
        <section className="rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
          <h4 className="text-sm font-semibold text-red-700">Zona de peligro</h4>
          <button type="button" onClick={() => { setErrorSuprimir(""); setModalSuprimir(true); }}
            className="mt-3 h-10 px-4 rounded-xl border border-red-200 bg-white text-sm font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-2">
            <Trash2 size={15} /> Borrar este contacto (derecho de supresión)
          </button>
        </section>
      ) : (
        <p className="text-xs text-slate-500">Si esta persona pide que borren sus datos (derecho de supresión), pedíselo a tu contacto de Deenex.</p>
      )}

      {modalConsentimiento && (
        <ModalConsentimiento
          modo={modalConsentimiento}
          busy={guardandoConsentimiento}
          error={errorConsentimiento}
          enNoContactar={enNoContactar}
          onConfirmar={(c) => void cambiarConsentimiento(c)}
          onCancelar={() => setModalConsentimiento(null)}
        />
      )}
      {modalSuprimir && (
        <ModalSuprimir
          yaEnNoContactar={bajasActivas.some((b) => b.simulado !== true)}
          busy={suprimiendo}
          error={errorSuprimir}
          onConfirmar={(s) => void suprimir(s)}
          onCancelar={() => setModalSuprimir(false)}
        />
      )}
    </div>
  );
}

function Dato({ label, valor }: { label: string; valor: ReactNode }) {
  const vacio = valor === "" || valor === null || valor === undefined;
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className={`break-words ${vacio ? "text-slate-400" : "text-slate-900"}`}>{vacio ? "—" : valor}</dd>
    </div>
  );
}
